import { inviteFamily } from '@api/Families'
import { invitesApi } from '@api/swagger'
import {
  InviteReturningFamilyFormCard,
  ProgramData,
} from '@components/Card/InviteReturningFamilyFormCard'
import { CanAccess } from '@components/Elements/Access'
import { Page } from '@components/Elements/PageMargins'
import { LicenseFeesNoticeModal } from '@components/Modals/LicenseFeesNoticeModal'
import PreviousInvitationsModal from '@components/Modals/PreviousInvitationsModal'
import { SendInvitationConfirmationModal } from '@components/Modals/SendInvitationConfirmationModal'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '@hooks/useLoadingContext'
import { useLoadingIds } from '@hooks/useLoadingIds'
import Box from '@mui/material/Box'
import {
  EnrollmentInviteCreate,
  InvitationSource,
  ProgramInvite,
  ProgramType,
} from '@swagger'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import TitleContext from 'TitleContext'

interface FormData extends EnrollmentInviteCreate {
  programs: Array<ProgramInvite & { programType: string; dateProgram: Date }>
}

export const InviteReturningFamily: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()

  const { handleError } = useAppSnackbar()
  const { useTitleEffect } = useContext(TitleContext)

  const title = t('Communities.InviteReturningFamily.Header', 'Families')
  useTitleEffect(title)

  const [isLicenseFeesNoticeModalOpen, setIsLicenseFeesNoticeModalOpen] =
    useState(true)
  const [
    isSendInvitationConfirmationModalOpen,
    setIsSendInvitationConfirmationModalOpen,
  ] = useState(false)
  const [isPreviousInvitationsModalOpen, setIsPreviousInvitationsModalOpen] =
    useState(false)

  const [formData, setFormData] = useState<FormData>()

  const {
    InviteReturningFamily: {
      inviteFamily: inviteFamilyLoadingId,
      validateInvitation: validateInvitationLoadingId,
    },
  } = useLoadingIds()

  const sendFamilyInvitation = async () => {
    try {
      await inviteFamily({
        parentEmail: formData?.parentEmail ?? '',
        parentFirstName: formData?.parentFirstName ?? '',
        parentLastName: formData?.parentLastName ?? '',
        communityKey: formData?.communityKey ?? 0,
        programs: formData?.programs ?? [],
        potentialInviteKey: formData?.potentialInviteKey,
        source: InvitationSource.ReturningFamily,
        parentUserKey: formData?.parentUserKey,
      })

      navigate({
        pathname: `/communities/community-details/${state?.communityId}/awaiting-decisions`,
      })
    } catch (e) {
      await handleError(e)
    }
  }

  const {
    triggerFetch: triggerInviteFamily,
    isLoading: isInviteFamilyLoading,
  } = useLoadingContext({
    asyncFunction: sendFamilyInvitation,
    loadingId: inviteFamilyLoadingId,
  })

  const validatePreviousInvitations = async (
    email: string,
    programKeys: number[]
  ) => {
    try {
      const { hasPreviousInvitations } = await invitesApi.validateInvitation({
        body: {
          email,
          programKeys,
        },
      })

      if (hasPreviousInvitations) {
        setIsPreviousInvitationsModalOpen(true)
        return
      }

      setIsSendInvitationConfirmationModalOpen(true)
    } catch (error) {
      await handleError(error)
    }
  }
  const {
    triggerFetch: triggerValidatePreviousInvitations,
    isLoading: isValidatePreviousInvitationsLoading,
  } = useLoadingContext({
    asyncFunction: validatePreviousInvitations,
    loadingId: validateInvitationLoadingId,
  })

  const handleSubmit = () => {
    triggerInviteFamily()
  }

  const handleSendInviteClick = async (data: { programs: ProgramData[] }) => {
    setFormData(() => ({
      parentEmail: state.parentEmail,
      parentFirstName: state.parentFirstName,
      parentLastName: state.parentLastName,
      parentUserKey: state.parentUserKey,
      source: InvitationSource.ReturningFamily,
      communityKey: state.communityId,
      potentialInviteKey: state.potentialInviteKey,
      programs: data.programs,
    }))

    triggerValidatePreviousInvitations(
      state.parentEmail,
      data.programs.map(({ programKey }) => programKey)
    )
  }

  const handleAcceptPreviousInvitations = () => {
    setIsPreviousInvitationsModalOpen(false)
    setIsSendInvitationConfirmationModalOpen(true)
  }

  const handleCancel = () => {
    navigate({
      pathname: `/communities/community-details/${state?.communityId}/awaiting-decisions`,
    })
  }

  const handleCloseModal = () => {
    setIsLicenseFeesNoticeModalOpen(false)
  }

  return (
    <Page>
      <CanAccess I="families" on="Feature">
        <CanAccess I="invite" on="Family">
          <Box>
            {isLicenseFeesNoticeModalOpen && (
              <LicenseFeesNoticeModal onAccept={handleCloseModal} />
            )}

            {isPreviousInvitationsModalOpen && (
              <PreviousInvitationsModal
                showModal
                handleConfirmationCancel={() =>
                  setIsPreviousInvitationsModalOpen(false)
                }
                onAccept={handleAcceptPreviousInvitations}
              />
            )}

            {isSendInvitationConfirmationModalOpen && (
              <SendInvitationConfirmationModal
                onCancel={() => setIsSendInvitationConfirmationModalOpen(false)}
                onConfirm={() => {
                  setIsPreviousInvitationsModalOpen(false)
                  setIsSendInvitationConfirmationModalOpen(false)
                  handleSubmit()
                }}
                parentEmail={state.parentEmail ?? ''}
                programs={
                  formData?.programs.map((program) => ({
                    programType: program.programType as ProgramType,
                    semesterOneStartDate: program.dateProgram,
                  })) ?? []
                }
              />
            )}

            <InviteReturningFamilyFormCard
              parentFirstName={state?.parentFirstName ?? ''}
              parentLastName={state?.parentLastName ?? ''}
              parentEmail={state?.parentEmail ?? ''}
              communityName={state?.communityName ?? ''}
              communityKey={state?.communityId ?? 0}
              previousPrograms={state?.previousPrograms ?? []}
              disableSendButton={
                isInviteFamilyLoading || isValidatePreviousInvitationsLoading
              }
              onCancel={handleCancel}
              onSubmit={handleSendInviteClick}
            />
          </Box>
        </CanAccess>
      </CanAccess>
    </Page>
  )
}
